import { REQUIREMENTS, isOfficeAvailable, isSupported, requirementLabel } from './capabilities.js';
import { failed, ok, unsupported, type HostResult } from './result.js';

export interface SlideRef {
  /** PowerPoint's own slide id, matching `p:sldId/@id` prefixed with `slide-`. */
  readonly id: string;
  readonly index: number;
  readonly layoutName?: string;
}

/**
 * The marker shape SlideForge puts on a quiz slide.
 *
 * SPIKE 5: PowerPoint's Office.js has no CustomXmlParts API, so the
 * authoritative quiz data lives in document settings. The slide still needs to
 * show *something*, because a lecturer opening the deck in plain PowerPoint
 * must see a sensible slide rather than a blank one — and because a quiz that
 * does not appear in the slide sorter is a quiz the author forgets exists.
 */
export const QUIZ_MARKER_TAG = 'SLIDEFORGE_QUIZ';
export const QUIZ_MARKER_NAME = 'SlideForge Quiz';
export const INTERACTION_MARKER_TAG = 'SLIDEFORGE_INTERACTION';
export const INTERACTION_MARKER_NAME = 'SlideForge Interaction';

/**
 * Lists the slides in the presentation.
 *
 * Requirement: `PowerPointApi 1.3`. Degradation: the caller falls back to the
 * slide list parsed out of the deck bytes, which it already has — slower to
 * refresh, but complete.
 */
export async function listSlides(): Promise<HostResult<SlideRef[]>> {
  if (!isOfficeAvailable()) return failed('Office.js is not available.');
  if (!isSupported(REQUIREMENTS.powerPointBase)) {
    return unsupported(
      requirementLabel(REQUIREMENTS.powerPointBase),
      'SlideForge will use the slide list read from the file instead.',
    );
  }

  try {
    return await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items/id');
      await context.sync();
      return ok(
        slides.items.map((slide, index) => ({
          id: `slide-${slide.id}`,
          index,
        })),
      );
    });
  } catch (error) {
    return failed(error instanceof Error ? error.message : String(error));
  }
}

/**
 * The slide the author currently has selected.
 *
 * Requirement: `PowerPointApi 1.3`. Degradation: the caller keeps its own
 * "current slide" from the last explicit selection in the task pane.
 */
export async function getSelectedSlide(): Promise<HostResult<SlideRef | undefined>> {
  if (!isOfficeAvailable()) return failed('Office.js is not available.');
  if (!isSupported(REQUIREMENTS.powerPointBase)) {
    return unsupported(
      requirementLabel(REQUIREMENTS.powerPointBase),
      'Choose the slide from the SlideForge list instead.',
    );
  }

  try {
    return await PowerPoint.run(async (context) => {
      const selected = context.presentation.getSelectedSlides();
      selected.load('items/id');
      const all = context.presentation.slides;
      all.load('items/id');
      await context.sync();

      const first = selected.items[0];
      if (!first) return ok(undefined);
      const index = all.items.findIndex((s) => s.id === first.id);
      return ok({ id: `slide-${first.id}`, index: index === -1 ? 0 : index });
    });
  } catch (error) {
    return failed(error instanceof Error ? error.message : String(error));
  }
}

/**
 * Fires when the author selects a different slide.
 *
 * Requirement: `Office.EventType.DocumentSelectionChanged` on the base context.
 * Degradation: returns a no-op unsubscribe and the pane simply does not follow
 * the selection; the author picks the slide from the list instead.
 */
export function onSelectionChanged(listener: () => void): () => void {
  if (!isOfficeAvailable()) return () => undefined;
  try {
    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      listener,
    );
    return () => {
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler: listener },
        );
      } catch {
        // Removing a handler on a closing document throws; nothing to do.
      }
    };
  } catch {
    return () => undefined;
  }
}

/**
 * Adds the visible marker to a quiz slide, or updates the one already there.
 *
 * Requirement: `PowerPointApi 1.4` for shape insertion. Degradation: the quiz
 * still works — the data lives in settings — but the slide looks untouched in
 * PowerPoint, so the caller warns the author that the quiz will not be visible
 * in the slide sorter on this version.
 */
export async function upsertQuizMarker(
  slideId: string,
  title: string,
): Promise<HostResult<void>> {
  if (!isOfficeAvailable()) return failed('Office.js is not available.');
  if (!isSupported(REQUIREMENTS.powerPointShapes)) {
    return unsupported(
      requirementLabel(REQUIREMENTS.powerPointShapes),
      'The quiz is saved, but this version of PowerPoint cannot show a marker on the slide.',
    );
  }

  const rawId = slideId.replace(/^slide-/, '');
  try {
    return await PowerPoint.run(async (context) => {
      const slide = context.presentation.slides.getItem(rawId);
      const shapes = slide.shapes;
      shapes.load('items/name');
      await context.sync();

      const existing = shapes.items.find((s) => s.name === QUIZ_MARKER_NAME);
      if (existing) {
        existing.textFrame.textRange.text = markerText(title);
      } else {
        const box = shapes.addTextBox(markerText(title), {
          left: 40,
          top: 40,
          width: 640,
          height: 80,
        });
        box.name = QUIZ_MARKER_NAME;
      }
      await context.sync();
      return ok(undefined);
    });
  } catch (error) {
    return failed(error instanceof Error ? error.message : String(error));
  }
}

/**
 * Adds or updates the visible plain-PowerPoint representation of an M8 slide.
 *
 * Verified 2026-08-21 against Microsoft Learn: ShapeCollection.addTextBox and
 * ShapeAddOptions are GA in PowerPointApi 1.4. Real-host compatibility remains
 * unverified and is recorded in docs/compat/m8-interactions.md.
 */
export async function upsertInteractionMarker(
  slideId: string,
  title: string,
  type: string,
): Promise<HostResult<void>> {
  if (!isOfficeAvailable()) return failed('Office.js is not available.');
  if (!isSupported(REQUIREMENTS.powerPointShapes)) {
    return unsupported(
      requirementLabel(REQUIREMENTS.powerPointShapes),
      'The interaction is saved, but this version of PowerPoint cannot show a marker on the slide.',
    );
  }
  const rawId = slideId.replace(/^slide-/, '');
  try {
    return await PowerPoint.run(async (context) => {
      const shapes = context.presentation.slides.getItem(rawId).shapes;
      shapes.load('items/name');
      await context.sync();
      const text = `${title || 'Interaksi'}\n(SlideForge ${type} interaction — edit it in the SlideForge pane)`;
      const existing = shapes.items.find((shape) => shape.name === INTERACTION_MARKER_NAME);
      if (existing) existing.textFrame.textRange.text = text;
      else {
        const box = shapes.addTextBox(text, { left: 40, top: 40, width: 640, height: 80 });
        box.name = INTERACTION_MARKER_NAME;
      }
      await context.sync();
      return ok(undefined);
    });
  } catch (error) {
    return failed(error instanceof Error ? error.message : String(error));
  }
}

export async function removeInteractionMarker(slideId: string): Promise<HostResult<void>> {
  if (!isOfficeAvailable()) return failed('Office.js is not available.');
  if (!isSupported(REQUIREMENTS.powerPointShapes)) {
    return unsupported(
      requirementLabel(REQUIREMENTS.powerPointShapes),
      'The interaction data was removed, but this version of PowerPoint cannot remove its marker.',
    );
  }
  const rawId = slideId.replace(/^slide-/, '');
  try {
    return await PowerPoint.run(async (context) => {
      const shapes = context.presentation.slides.getItem(rawId).shapes;
      shapes.load('items/name');
      await context.sync();
      shapes.items.find((shape) => shape.name === INTERACTION_MARKER_NAME)?.delete();
      await context.sync();
      return ok(undefined);
    });
  } catch (error) {
    return failed(error instanceof Error ? error.message : String(error));
  }
}

function markerText(title: string): string {
  return `${title || 'Kuis'}\n(SlideForge quiz — edit it in the SlideForge pane)`;
}
